import type { CreateColaboradorDto } from '../../services/colaboradores.service';
import FormattedInput from '../FormattedInput';
import CpfInput from '../CpfInput';
import SearchableSelect from '../SearchableSelect';

interface ColaboradorFormProps {
    formData: CreateColaboradorDto;
    onChange: (data: CreateColaboradorDto) => void;
    tiposContrato?: { id: number; descricao?: string; valor: string }[];
    formacoes?: { id: number; descricao?: string; valor: string }[];
    setores?: { id: number; nome: string }[];
    cargos?: { id: number; nome: string }[];
    isLoading?: boolean;
    isEditing?: boolean;
    onSetoresChange?: (setores: { id: number; nome: string }[]) => void;
    onCargosChange?: (cargos: { id: number; nome: string }[]) => void;
}

export default function ColaboradorForm({
    formData,
    onChange,
    tiposContrato,
    formacoes,
    setores,
    cargos,
    isLoading = false,
    isEditing = false,
}: ColaboradorFormProps) {
    const isEditMode = isEditing || !!formData.id;

    const handleChange = (field: keyof CreateColaboradorDto, value: unknown) => {
        onChange({ ...formData, [field]: value });
    };

    return (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo <span className="text-red-500 font-bold">*</span>
                </label>
                <input
                    type="text"
                    required
                    value={formData.nome || ''}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    className="input-field"
                    disabled={isLoading}
                />
            </div>
            <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Social <span className="text-gray-400 font-normal">(Opcional)</span>
                </label>
                <input
                    type="text"
                    value={formData.nome_social || ''}
                    onChange={(e) => handleChange('nome_social', e.target.value)}
                    placeholder="Nome social para documentos oficiais"
                    className="input-field"
                    disabled={isLoading}
                />
            </div>
            <div>
                <CpfInput
                    label="CPF"
                    required
                    value={formData.cpf || ''}
                    onChange={(value) => handleChange('cpf', value)}
                    disabled={isEditMode || isLoading}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    RG
                </label>
                <input
                    type="text"
                    value={formData.rg || ''}
                    onChange={(e) => handleChange('rg', e.target.value)}
                    className="input-field"
                    disabled={isLoading}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Nascimento
                </label>
                <input
                    type="date"
                    value={formData.data_nascimento || ''}
                    onChange={(e) => handleChange('data_nascimento', e.target.value)}
                    className="input-field"
                    disabled={isLoading}
                />
            </div>
            <div>
                <SearchableSelect
                    label="Sexo Biológico"
                    options={[
                        { id: '', label: 'Selecione' },
                        { id: 'M', label: 'Masculino' },
                        { id: 'F', label: 'Feminino' },
                    ]}
                    value={formData.sexo || ''}
                    onChange={(value) => handleChange('sexo', value as string)}
                    placeholder="Selecione"
                />
            </div>
            <div>
                <SearchableSelect
                    label="Estado Civil"
                    options={[
                        { id: '', label: 'Selecione' },
                        { id: 'Solteiro(a)', label: 'Solteiro(a)' },
                        { id: 'Casado(a)', label: 'Casado(a)' },
                        { id: 'União Estável', label: 'União Estável' },
                        { id: 'Divorciado(a)', label: 'Divorciado(a)' },
                        { id: 'Viúvo(a)', label: 'Viúvo(a)' },
                    ]}
                    value={formData.estado_civil || ''}
                    onChange={(value) => handleChange('estado_civil', value as string)}
                    placeholder="Selecione"
                />
            </div>
            <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                </label>
                <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="input-field"
                    disabled={isLoading}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                </label>
                <FormattedInput
                    type="phone"
                    value={formData.telefone || ''}
                    onChange={(value) => handleChange('telefone', value)}
                />
            </div>
            <div>
                <SearchableSelect
                    label="Formação Acadêmica"
                    options={[
                        { id: '', label: 'Selecione' },
                        ...(formacoes?.map((formacao) => ({
                            id: formacao.id.toString(),
                            label: formacao.descricao || formacao.valor,
                        })) || []),
                    ]}
                    value={formData.formacao_academica_id ? formData.formacao_academica_id.toString() : ''}
                    onChange={(value) => handleChange('formacao_academica_id', value ? parseInt(value as string) : undefined)}
                    placeholder="Selecione"
                />
            </div>
            <div>
                <SearchableSelect
                    label="Tipo de Contrato"
                    options={[
                        { id: '', label: 'Selecione' },
                        ...(tiposContrato?.map((tipo) => ({
                            id: tipo.id.toString(),
                            label: tipo.descricao || tipo.valor,
                        })) || []),
                    ]}
                    value={formData.tipo_id ? formData.tipo_id.toString() : ''}
                    onChange={(value) => handleChange('tipo_id', value ? parseInt(value as string) : undefined)}
                    placeholder="Selecione"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Admissão <span className="text-red-500 font-bold">*</span>
                </label>
                <input
                    type="date"
                    required
                    value={formData.data_admissao || ''}
                    onChange={(e) => handleChange('data_admissao', e.target.value)}
                    className="input-field"
                    disabled={isLoading}
                />
            </div>
            <div>
                <SearchableSelect
                    label="Regime de Contratação"
                    options={[
                        { id: '', label: 'Selecione' },
                        { id: 'CLT', label: 'CLT' },
                        { id: 'PJ', label: 'PJ' },
                        { id: 'Estágio', label: 'Estágio' },
                        { id: 'Temporário', label: 'Temporário' },
                        { id: 'Autônomo', label: 'Autônomo' },
                        { id: 'Jovem Aprendiz', label: 'Jovem Aprendiz' },
                    ]}
                    value={formData.regime_contratacao || ''}
                    onChange={(value) => handleChange('regime_contratacao', value as string)}
                    placeholder="Selecione"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Demissão
                </label>
                <input
                    type="date"
                    value={formData.data_demissao || ''}
                    onChange={(e) => handleChange('data_demissao', e.target.value)}
                    className="input-field"
                    disabled={isLoading}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Matrícula e-Social
                </label>
                <input
                    type="text"
                    value={formData.matricula_esocial || ''}
                    onChange={(e) => handleChange('matricula_esocial', e.target.value)}
                    className="input-field"
                    disabled={isLoading}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Setor {isEditMode ? (
                        <span className="text-xs text-gray-400 font-normal">(Somente visualização)</span>
                    ) : (
                        <span className="text-red-500 font-bold">*</span>
                    )}
                </label>
                {isEditMode ? (
                    <input
                        type="text"
                        disabled
                        readOnly
                        tabIndex={-1}
                        value={
                            formData.setor_nome ||
                            setores?.find((s) => s.id === formData.setor_id)?.nome ||
                            (formData.setor_id ? `Setor #${formData.setor_id}` : 'Não informado')
                        }
                        className="input-field bg-gray-100 text-gray-700 cursor-not-allowed border-gray-200 font-medium select-none pointer-events-none"
                        title="O setor do colaborador não pode ser alterado por este formulário."
                    />
                ) : (
                    <SearchableSelect
                        options={[
                            { id: '', label: 'Selecione o setor' },
                            ...(setores?.map((setor) => ({
                                id: setor.id.toString(),
                                label: setor.nome,
                            })) || []),
                        ]}
                        value={formData.setor_id ? formData.setor_id.toString() : ''}
                        onChange={(value) => {
                            handleChange('setor_id', value ? parseInt(value as string) : undefined);
                        }}
                        placeholder="Selecione o setor"
                    />
                )}
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cargo {isEditMode ? (
                        <span className="text-xs text-gray-400 font-normal">(Somente visualização)</span>
                    ) : (
                        <span className="text-red-500 font-bold">*</span>
                    )}
                </label>
                {isEditMode ? (
                    <input
                        type="text"
                        disabled
                        readOnly
                        tabIndex={-1}
                        value={
                            formData.cargo_nome ||
                            cargos?.find((c) => c.id === formData.cargo_id)?.nome ||
                            (formData.cargo_id ? `Cargo #${formData.cargo_id}` : 'Não informado')
                        }
                        className="input-field bg-gray-100 text-gray-700 cursor-not-allowed border-gray-200 font-medium select-none pointer-events-none"
                        title="O cargo do colaborador não pode ser alterado por este formulário."
                    />
                ) : (
                    <SearchableSelect
                        options={[
                            { id: '', label: 'Selecione o cargo' },
                            ...(cargos?.map((cargo) => ({
                                id: cargo.id.toString(),
                                label: cargo.nome,
                            })) || []),
                        ]}
                        value={formData.cargo_id ? formData.cargo_id.toString() : ''}
                        onChange={(value) => {
                            handleChange('cargo_id', value ? parseInt(value as string) : undefined);
                        }}
                        placeholder="Selecione o cargo"
                    />
                )}
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Último Periódico
                </label>
                <input
                    type="date"
                    value={formData.ultimo_periodico || ''}
                    onChange={(e) => handleChange('ultimo_periodico', e.target.value)}
                    className="input-field"
                    disabled={isLoading}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tempo Periódico (meses)
                </label>
                <input
                    type="number"
                    min="1"
                    value={formData.tempo_periodico || ''}
                    onChange={(e) => handleChange('tempo_periodico', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="input-field"
                    disabled={isLoading}
                />
            </div>

            {/* Bloco 5: Flags eSocial & Inclusão */}
            <div className="col-span-2 xl:col-span-4 border-t border-gray-100 pt-3 mt-1 flex flex-wrap gap-6 items-center">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={formData.pcd === true}
                        onChange={(e) => handleChange('pcd', e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                        disabled={isLoading}
                    />
                    <span className="text-xs font-semibold text-gray-700">PCD (Pessoa com Deficiência)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={formData.enviar_esocial !== false}
                        onChange={(e) => handleChange('enviar_esocial', e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                        disabled={isLoading}
                    />
                    <span className="text-xs font-semibold text-gray-700">Enviar para o e-Social</span>
                </label>
            </div>
        </div>
    );
}
